import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Save,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

interface JournalLine {
  id: string;
  accountId: string;
  accountCode?: string;
  accountName?: string;
  description: string;
  debit: number;
  credit: number;
}

// TDMS Reference Types
const REFERENCE_TYPES = [
  { value: 'MANUAL', label: 'Manual Entry' },
  { value: 'ADJUSTMENT', label: 'Adjustment' },
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'SALE', label: 'Sale' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'RECEIPT', label: 'Receipt' },
  { value: 'GRN', label: 'Goods Receipt Note' },
  { value: 'DELIVERY', label: 'Delivery Note' },
  { value: 'CLOSING', label: 'Closing Entry' },
];

export function TradingJournalEntryFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('tradingAccounting');
  const { user } = useAuthStore();
  const isEditing = !!id;

  const [date, setDate] = useState(new Date().toISOString().split('T')[0] ?? '');
  const [description, setDescription] = useState('');
  const [referenceType, setReferenceType] = useState<string>('MANUAL');
  const [currency, setCurrency] = useState('TRY');
  const [lines, setLines] = useState<JournalLine[]>([
    { id: '1', accountId: '', description: '', debit: 0, credit: 0 },
    { id: '2', accountId: '', description: '', debit: 0, credit: 0 },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing entry if editing
  const { data: existingEntry, isLoading: isLoadingEntry } = trpc.tradingJournalEntry.byId.useQuery(
    id!,
    { enabled: isEditing }
  );

  // All active accounts for selection
  const { data: allAccounts } = trpc.tradingAccount.list.useQuery({
    isActive: true,
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.tradingJournalEntry.create.useMutation({
    onSuccess: (data) => {
      utils.tradingJournalEntry.list.invalidate();
      utils.tradingJournalEntry.stats.invalidate();
      navigate(`/trading/accounting/journal-entries/${data.id}`);
    },
    onError: (err) => {
      setError(err.message);
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.tradingJournalEntry.update.useMutation({
    onSuccess: () => {
      utils.tradingJournalEntry.list.invalidate();
      utils.tradingJournalEntry.byId.invalidate(id);
      navigate(`/trading/accounting/journal-entries/${id}`);
    },
    onError: (err) => {
      setError(err.message);
      setIsSubmitting(false);
    },
  });

  // Load existing entry data
  useEffect(() => {
    if (existingEntry) {
      setDate(new Date(existingEntry.date).toISOString().split('T')[0] ?? '');
      setDescription(existingEntry.description);
      setReferenceType(existingEntry.referenceType || 'MANUAL');
      setCurrency(existingEntry.currency);
      setLines(
        existingEntry.lines.map((line: {
          accountId: string;
          account: { code: string; name: string };
          description?: string | null;
          debit: number;
          credit: number;
        }, i: number) => ({
          id: String(i + 1),
          accountId: line.accountId,
          accountCode: line.account.code,
          accountName: line.account.name,
          description: line.description || '',
          debit: line.debit,
          credit: line.credit,
        }))
      );
    }
  }, [existingEntry]);

  const addLine = () => {
    setLines([
      ...lines,
      { id: String(Date.now()), accountId: '', description: '', debit: 0, credit: 0 },
    ]);
  };

  const removeLine = (id: string) => {
    if (lines.length > 2) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  const updateLine = (id: string, field: keyof JournalLine, value: string | number) => {
    setLines(
      lines.map((line) => {
        if (line.id === id) {
          const updated = { ...line, [field]: value };
          // Clear the other amount when one is entered
          if (field === 'debit' && Number(value) > 0) {
            updated.credit = 0;
          } else if (field === 'credit' && Number(value) > 0) {
            updated.debit = 0;
          }
          // Set account info when account is selected
          if (field === 'accountId') {
            const account = allAccounts?.accounts.find(a => a.id === value);
            if (account) {
              updated.accountCode = account.code;
              updated.accountName = account.name;
            }
          }
          return updated;
        }
        return line;
      })
    );
  };

  const totalDebit = lines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (line.credit || 0), 0);
  const difference = Math.abs(totalDebit - totalCredit);
  const isBalanced = difference < 0.01;

  const validateForm = (): boolean => {
    setError(null);

    if (!date) {
      setError(t('journalForm.errorDateRequired', 'Date is required'));
      return false;
    }

    if (!description.trim()) {
      setError(t('journalForm.errorDescriptionRequired', 'Description is required'));
      return false;
    }

    if (lines.length < 2) {
      setError(t('journalForm.errorMinLines', 'At least two lines are required'));
      return false;
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;
      if (!line.accountId) {
        setError(t('journalForm.errorAccountRequired', 'Line {{number}}: Account is required', { number: i + 1 }));
        return false;
      }
      if (line.debit === 0 && line.credit === 0) {
        setError(t('journalForm.errorAmountRequired', 'Line {{number}}: Either debit or credit amount is required', { number: i + 1 }));
        return false;
      }
      if (line.debit > 0 && line.credit > 0) {
        setError(t('journalForm.errorBothAmounts', 'Line {{number}}: Cannot have both debit and credit', { number: i + 1 }));
        return false;
      }
    }

    if (!isBalanced) {
      setError(t('journalForm.errorNotBalanced', 'Total debits ({{debit}}) must equal total credits ({{credit}})', {
        debit: formatCurrency(totalDebit, currency),
        credit: formatCurrency(totalCredit, currency),
      }));
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!validateForm() || !user) return;

    setIsSubmitting(true);

    const lineData = lines.map((line) => ({
      accountId: line.accountId,
      description: line.description || undefined,
      debit: line.debit || 0,
      credit: line.credit || 0,
    }));

    if (isEditing) {
      updateMutation.mutate({
        id: id!,
        date: new Date(date || new Date()),
        description,
        referenceType: referenceType as 'MANUAL' | 'ADJUSTMENT' | 'PURCHASE' | 'SALE' | 'PAYMENT' | 'RECEIPT' | 'GRN' | 'DELIVERY' | 'CLOSING',
        currency,
        lines: lineData,
      });
    } else {
      createMutation.mutate({
        date: new Date(date || new Date()),
        description,
        referenceType: referenceType as 'MANUAL' | 'ADJUSTMENT' | 'PURCHASE' | 'SALE' | 'PAYMENT' | 'RECEIPT' | 'GRN' | 'DELIVERY' | 'CLOSING',
        currency,
        lines: lineData,
      });
    }
  };

  if (isEditing && isLoadingEntry) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isEditing && existingEntry?.status !== 'DRAFT') {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <p className="text-lg font-medium">{t('journalForm.cannotEdit', 'Cannot Edit Entry')}</p>
        <p className="text-muted-foreground mt-2">
          {t('journalForm.onlyDraftEditable', 'Only draft entries can be edited. This entry is {{status}}.', { status: existingEntry?.status.toLowerCase() })}
        </p>
        <Button variant="link" onClick={() => navigate(`/trading/accounting/journal-entries/${id}`)}>
          {t('journalForm.viewDetails', 'View Entry Details')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/trading/accounting')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? t('journalForm.editTitle', 'Edit Journal Entry') : t('journalForm.newTitle', 'New Journal Entry')}
            </h1>
            <p className="text-muted-foreground">
              {isEditing
                ? t('journalForm.editingEntry', 'Editing {{number}}', { number: existingEntry?.entryNumber })
                : t('journalForm.createNew', 'Create a new manual journal entry')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-8" onClick={() => navigate('/trading/accounting')}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button className="h-8" onClick={handleSave} disabled={isSubmitting || !isBalanced}>
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEditing ? t('journalForm.saveChanges', 'Save Changes') : t('journalForm.saveAsDraft', 'Save as Draft')}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Entry Details */}
      <div className="border border-border bg-card p-6">
        <h3 className="text-lg font-semibold mb-4">{t('journalForm.entryDetails', 'Entry Details')}</h3>
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="date">{t('journalForm.date', 'Date')} *</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('journalForm.referenceType', 'Reference Type')}</Label>
            <Combobox
              options={REFERENCE_TYPES.map((type) => ({
                value: type.value,
                label: type.label,
              }))}
              value={referenceType}
              onValueChange={setReferenceType}
              placeholder={t('journalForm.selectType', 'Select type')}
              searchPlaceholder={t('common.search', 'Search...')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('journalForm.currency', 'Currency')}</Label>
            <Combobox
              options={[
                { value: 'TRY', label: 'TRY' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'GBP', label: 'GBP' },
              ]}
              value={currency}
              onValueChange={setCurrency}
              placeholder={t('journalForm.selectCurrency', 'Select currency')}
              searchPlaceholder={t('common.search', 'Search...')}
            />
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label htmlFor="description">{t('journalForm.description', 'Description')} *</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('journalForm.descriptionPlaceholder', 'Enter entry description...')}
            />
          </div>
        </div>
      </div>

      {/* Journal Lines */}
      <div className="border border-border bg-card">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('journalForm.journalLines', 'Journal Lines')}</h3>
          <Button variant="outline" size="sm" className="h-8" onClick={addLine}>
            <Plus className="mr-2 h-4 w-4" />
            {t('journalForm.addLine', 'Add Line')}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium w-[300px]">{t('journalForm.account', 'Account')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('journalForm.lineDescription', 'Description')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium w-[150px]">{t('journalForm.debit', 'Debit')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium w-[150px]">{t('journalForm.credit', 'Credit')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium w-[60px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {lines.map((line) => (
                <tr key={line.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2">
                    <Combobox
                      options={allAccounts?.accounts.map((account) => ({
                        value: account.id,
                        label: `${account.code} - ${account.name}`,
                      })) || []}
                      value={line.accountId}
                      onValueChange={(value) => updateLine(line.id, 'accountId', value)}
                      placeholder={t('journalForm.selectAccount', 'Select account...')}
                      searchPlaceholder={t('journalForm.searchAccounts', 'Search accounts...')}
                      className="w-full"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={line.description}
                      onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                      placeholder={t('journalForm.lineDescriptionPlaceholder', 'Line description (optional)')}
                      className="w-full"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.debit || ''}
                      onChange={(e) => updateLine(line.id, 'debit', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="text-right font-mono"
                      disabled={line.credit > 0}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.credit || ''}
                      onChange={(e) => updateLine(line.id, 'credit', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="text-right font-mono"
                      disabled={line.debit > 0}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(line.id)}
                      disabled={lines.length <= 2}
                      className="text-muted-foreground hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-medium">
              <tr>
                <td className="px-4 py-3" colSpan={2}>
                  <span className="text-sm">{t('journalForm.totals', 'Totals')}</span>
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(totalDebit, currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(totalCredit, currency)}
                </td>
                <td></td>
              </tr>
              {!isBalanced && (
                <tr className="text-red-600">
                  <td className="px-4 py-2" colSpan={2}>
                    <span className="text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {t('journalForm.outOfBalance', 'Out of Balance')}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono" colSpan={2}>
                    {t('journalForm.difference', 'Difference')}: {formatCurrency(difference, currency)}
                  </td>
                  <td></td>
                </tr>
              )}
              {isBalanced && totalDebit > 0 && (
                <tr className="text-green-600">
                  <td className="px-4 py-2" colSpan={4}>
                    <span className="text-sm">{t('journalForm.entryBalanced', 'Entry is balanced')}</span>
                  </td>
                  <td></td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {/* Tips */}
      <div className="border border-blue-200 bg-blue-50 p-4 text-blue-700 text-sm">
        <p className="font-medium mb-1">{t('journalForm.tips', 'Tips')}:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>{t('journalForm.tip1', 'Each line can have either a debit OR a credit amount, not both')}</li>
          <li>{t('journalForm.tip2', 'Total debits must equal total credits for the entry to be valid')}</li>
          <li>{t('journalForm.tip3', 'The entry will be saved as a draft - you can post it from the detail page')}</li>
        </ul>
      </div>
    </div>
  );
}
